import React, { useCallback, useEffect, useRef, useState } from "react";
import "./ChatWindow.css";
import EmojiPicker from "emoji-picker-react";
import {
  getMessages,
  sendMessage,
  markConversationRead,
  uploadChatAttachments,
  deleteChatMessage,
} from "../../api/chatApi";
import { API_BASE_URL, WS_BASE_URL } from "../../config";

const normalize = (url) => {
  if (!url) return "/default-avatar.png";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${API_BASE_URL}${url}`;
};

const formatProfileStatus = (isoString) => {
  if (!isoString) return "";

  const lastSeen = new Date(isoString);

  if (Number.isNaN(lastSeen.getTime())) {
    return "";
  }

  const now = new Date();
  const diffMs = now.getTime() - lastSeen.getTime();

  if (diffMs >= 0 && diffMs <= 90 * 1000) {
    return "Ενεργώς";
  }

  const isToday = lastSeen.toDateString() === now.toDateString();

  const yesterday = new Date();
  yesterday.setDate(now.getDate() - 1);

  const isYesterday = lastSeen.toDateString() === yesterday.toDateString();

  const time = lastSeen.toLocaleTimeString("el-GR", {
    hour: "2-digit",
    minute: "2-digit",
  });

  if (isToday) {
    return `σήμερα στις ${time}`;
  }

  if (isYesterday) {
    return `χθες στις ${time}`;
  }

  return (
    lastSeen.toLocaleDateString("el-GR", {
      day: "2-digit",
      month: "2-digit",
    }) + `, ${time}`
  );
};

const EMOJI_UNIT =
  String.raw`(?:\p{Extended_Pictographic}|\p{Emoji_Presentation})(?:\uFE0F|\uFE0E)?(?:\p{Emoji_Modifier})?`;

const EMOJI_PATTERN =
  String.raw`(?:\p{Regional_Indicator}{2}|[0-9#*]\uFE0F?\u20E3|${EMOJI_UNIT}(?:\u200D${EMOJI_UNIT})*)`;

const EMOJI_SPLIT_REGEX = new RegExp(`(${EMOJI_PATTERN})`, "gu");
const EMOJI_TEST_REGEX = new RegExp(`^${EMOJI_PATTERN}$`, "u");

const isEmojiOnlyMessage = (value) => {
  const text = (value || "").trim();

  if (!text) {
    return false;
  }

  const parts = text
    .split(EMOJI_SPLIT_REGEX)
    .filter(Boolean)
    .filter((part) => part.trim());

  return parts.length > 0 && parts.every((part) => EMOJI_TEST_REGEX.test(part));
};

const renderMessageText = (value) => {
  const text = value || "";

  return text.split(EMOJI_SPLIT_REGEX).map((part, index) => {
    if (!part) {
      return null;
    }

    const isEmoji = EMOJI_TEST_REGEX.test(part);

    return (
      <span
        key={`${part}-${index}`}
        className={isEmoji ? "bubble-emoji" : undefined}
      >
        {part}
      </span>
    );
  });
};

const QUICK_REPLY_TEXTS = [
  "Είναι ακόμα διαθέσιμο;",
  "Πού μπορώ να το δω;",
  "Μπορώ να το δω σήμερα;",
  "Ποια είναι η τελική τιμή;",
  "Μπορείτε να μου στείλετε περισσότερες φωτογραφίες;",
];

const formatFileSize = (size) => {
  if (!size) {
    return "";
  }

  if (size < 1024 * 1024) {
    return `${Math.round(size / 1024)} KB`;
  }

  return `${(size / 1024 / 1024).toFixed(1)} MB`;
};

const isPreviewableMediaAttachment = (attachment) => {
  return (
    attachment &&
    attachment.status === "ready" &&
    attachment.url &&
    ["image", "video"].includes(attachment.attachment_type)
  );
};

const getMediaAttachmentsFromMessages = (messageList) => {
  return (messageList || []).flatMap((msg) => {
    const attachments = Array.isArray(msg.attachments) ? msg.attachments : [];

    return attachments
      .filter(isPreviewableMediaAttachment)
      .map((attachment) => ({
        ...attachment,
        message_id: msg.id || msg.client_temp_id,
      }));
  });
};

const renderChatAttachment = (attachment, onOpenMedia) => {
  if (!attachment) {
    return null;
  }

  if (attachment.status !== "ready" || !attachment.url) {
    return (
      <div key={attachment.id} className="chat-attachment-unavailable">
        Το αρχείο δεν είναι διαθέσιμο
      </div>
    );
  }

  if (attachment.attachment_type === "image") {
    return (
      <button
        key={attachment.id}
        type="button"
        className="chat-attachment-image-link"
        onClick={() => onOpenMedia?.(attachment.id)}
        aria-label="Open image"
      >
        <img
          src={attachment.thumbnail_url || attachment.url}
          alt={attachment.original_name || "image"}
          className="chat-attachment-image"
        />
      </button>
    );
  }

  if (attachment.attachment_type === "video") {
    return (
      <button
        key={attachment.id}
        type="button"
        className="chat-attachment-video-link"
        onClick={() => onOpenMedia?.(attachment.id)}
        aria-label="Open video"
      >
        {attachment.thumbnail_url ? (
          <img
            src={attachment.thumbnail_url}
            alt={attachment.original_name || "video"}
            className="chat-attachment-video-thumb"
          />
        ) : (
          <div className="chat-attachment-video-placeholder">
            <span className="chat-attachment-video-icon">🎬</span>
            <span className="chat-attachment-video-label">Βίντεο</span>
            <span className="chat-attachment-video-size">
              {formatFileSize(attachment.size)}
            </span>
          </div>
        )}

        <span className="chat-attachment-play">▶</span>
      </button>
    );
  }

  return (
    <a
      key={attachment.id}
      href={attachment.url}
      target="_blank"
      rel="noreferrer"
      className="chat-attachment-file"
    >
      <span className="chat-attachment-file-icon">📎</span>

      <span className="chat-attachment-file-info">
        <span className="chat-attachment-file-name">
          {attachment.original_name || "Αρχείο"}
        </span>

        <span className="chat-attachment-file-size">
          {formatFileSize(attachment.size)}
        </span>
      </span>
    </a>
  );
};

const getAttachmentReplyLabel = (attachmentType, originalName = "") => {
  if (attachmentType === "image") {
    return "📷 Φωτογραφία";
  }

  if (attachmentType === "video") {
    return "🎬 Βίντεο";
  }

  if (attachmentType === "file") {
    return `📎 ${originalName || "Αρχείο"}`;
  }

  return "Μήνυμα";
};

const getReplyPreviewText = (message) => {
  if (!message) {
    return "";
  }

  if (message.is_deleted || message.deleted_at) {
    return "Το μήνυμα διαγράφηκε";
  }

  const text = (message.text || "").trim();

  if (text) {
    return text;
  }

  if (message.attachment_type) {
    return getAttachmentReplyLabel(
      message.attachment_type,
      message.original_name
    );
  }

  const attachments = Array.isArray(message.attachments)
    ? message.attachments
    : [];

  const attachment =
    attachments.find((item) => item.status === "ready") || attachments[0];

  if (!attachment) {
    return "Μήνυμα";
  }

  return getAttachmentReplyLabel(
    attachment.attachment_type,
    attachment.original_name
  );
};

const getReplyAuthorName = (message, currentProfileId) => {
  const senderId = message?.sender?.id ?? message?.sender_id;

  if (senderId && String(senderId) === String(currentProfileId)) {
    return "Εσύ";
  }

  return message?.sender?.name || message?.sender_name || "Χρήστης";
};

const buildReplySnapshot = (message, currentProfileId) => {
  if (!message?.id) {
    return null;
  }

  const attachments = Array.isArray(message.attachments)
    ? message.attachments
    : [];

  const attachment = attachments[0];

  return {
    id: message.id,
    sender_id: message.sender?.id ?? message.sender_id,
    sender_name: getReplyAuthorName(message, currentProfileId),
    text: (message.text || "").trim(),
    is_deleted: Boolean(message.is_deleted || message.deleted_at),
    attachment_type: attachment?.attachment_type || null,
    original_name: attachment?.original_name || "",
  };
};

const mergeMessageIntoList = (messageList, incomingMessage) => {
  const incomingId = incomingMessage.id ? String(incomingMessage.id) : null;
  const incomingClientTempId = incomingMessage.client_temp_id
    ? String(incomingMessage.client_temp_id)
    : null;

  const existingIndex = messageList.findIndex((msg) => {
    const sameId =
      incomingId && msg.id && String(msg.id) === incomingId;

    const sameClientTempId =
      incomingClientTempId &&
      msg.client_temp_id &&
      String(msg.client_temp_id) === incomingClientTempId;

    return sameId || sameClientTempId;
  });

  const normalizedMessage = {
    ...incomingMessage,
    local_status: incomingMessage.local_status ?? null,
  };

  if (existingIndex === -1) {
    return [...messageList, normalizedMessage];
  }

  return messageList.map((msg, index) => {
    if (index !== existingIndex) {
      return msg;
    }

    return {
      ...msg,
      ...incomingMessage,
      local_status: incomingMessage.local_status ?? null,
    };
  });
};

const getOutgoingQueueKey = (conversationId, profileId) =>
  `chat-outgoing-${conversationId}-${profileId}`;

const readOutgoingQueue = (conversationId, profileId) => {
  try {
    const raw = localStorage.getItem(
      getOutgoingQueueKey(conversationId, profileId)
    );

    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw);

    return Array.isArray(parsed) ? parsed : [];
  } catch (err) {
    console.error("Ошибка чтения очереди сообщений:", err);
    return [];
  }
};

const writeOutgoingQueue = (conversationId, profileId, queue) => {
  try {
    localStorage.setItem(
      getOutgoingQueueKey(conversationId, profileId),
      JSON.stringify(queue)
    );
  } catch (err) {
    console.error("Ошибка сохранения очереди сообщений:", err);
  }
};

const upsertOutgoingMessage = (conversationId, profileId, message) => {
  const queue = readOutgoingQueue(conversationId, profileId);

  const exists = queue.some(
    (item) => item.client_temp_id === message.client_temp_id
  );

  const nextQueue = exists
    ? queue.map((item) =>
        item.client_temp_id === message.client_temp_id ? message : item
      )
    : [...queue, message];

  writeOutgoingQueue(conversationId, profileId, nextQueue);
};

const removeOutgoingMessage = (conversationId, profileId, clientTempId) => {
  const queue = readOutgoingQueue(conversationId, profileId);

  const nextQueue = queue.filter(
    (item) => item.client_temp_id !== clientTempId
  );

  writeOutgoingQueue(conversationId, profileId, nextQueue);
};

const mergeMessagesWithOutgoingQueue = (
  conversationId,
  profileId,
  serverMessages
) => {
  const queue = readOutgoingQueue(conversationId, profileId);

  if (!queue.length) {
    return serverMessages || [];
  }

  const serverClientTempIds = new Set(
    (serverMessages || [])
      .map((msg) => msg.client_temp_id)
      .filter(Boolean)
      .map(String)
  );

  const pendingQueue = queue.filter(
    (item) => !serverClientTempIds.has(String(item.client_temp_id))
  );

  if (pendingQueue.length !== queue.length) {
    writeOutgoingQueue(conversationId, profileId, pendingQueue);
  }

  return pendingQueue.reduce(
    (result, queuedMessage) => mergeMessageIntoList(result, queuedMessage),
    serverMessages || []
  );
};

const ChatWindow = ({
  token,
  conversation,
  currentProfileId,
  onBack,
  onConversationRead,
}) => {
  const [messages, setMessages] = useState([]);
  const [messagesLoaded, setMessagesLoaded] = useState(false);
  const [text, setText] = useState("");
  const [socket, setSocket] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  const [mediaViewerIndex, setMediaViewerIndex] = useState(null);
  const [openMessageMenuId, setOpenMessageMenuId] = useState(null);
  const [deletingMessageId, setDeletingMessageId] = useState(null);
  const [replyingToMessage, setReplyingToMessage] = useState(null);
  const [mobileActionMessage, setMobileActionMessage] = useState(null);

  const socketRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const shouldReconnectRef = useRef(true);

  const messagesEndRef = useRef(null);
  const pendingDeliveryIdsRef = useRef(new Set());
  const messageInputRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const attachmentMenuRef = useRef(null);
  const mediaInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraImageInputRef = useRef(null);
  const cameraVideoInputRef = useRef(null);
  const longPressTimerRef = useRef(null);
  const previousMessagesLengthRef = useRef(0);
  const canMarkConversationRead = useCallback(() => {
    return (
      document.visibilityState === "visible" &&
      document.hasFocus()
    );
  }, []);

  const markConversationReadIfActive = useCallback(async () => {
    if (!conversation?.id || !token) {
      return;
    }

    if (!canMarkConversationRead()) {
      return;
    }

    await markConversationRead(conversation.id, token);
    onConversationRead && onConversationRead(conversation.id);

    window.dispatchEvent(new Event("refresh-unread-count"));
    window.dispatchEvent(new Event("refresh-chat-list"));
  }, [
    conversation?.id,
    token,
    onConversationRead,
    canMarkConversationRead,
  ]);

  useEffect(() => {
    if (!openMessageMenuId) {
      return;
    }

    const closeMessageMenu = () => {
      setOpenMessageMenuId(null);
    };

    document.addEventListener("click", closeMessageMenu);

    return () => {
      document.removeEventListener("click", closeMessageMenu);
    };
  }, [openMessageMenuId]);

  // Прокрутка вниз при добавлении сообщения
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const flushPendingDeliveryReceipts = useCallback(() => {
    const ws = socketRef.current;

    if (!ws || ws.readyState !== WebSocket.OPEN) {
      return false;
    }

    const messageIds = Array.from(pendingDeliveryIdsRef.current);

    if (!messageIds.length) {
      return true;
    }

    ws.send(
      JSON.stringify({
        type: "message.delivered",
        message_ids: messageIds,
      })
    );

    pendingDeliveryIdsRef.current.clear();
    return true;
  }, []);

const sendDeliveryReceiptsForMessages = useCallback((messageList) => {
  const incomingMessageIds = (messageList || [])
    .filter((msg) => {
      const senderId = msg.sender?.id ?? msg.sender_id;

      return (
        msg.id &&
        !msg.is_system &&
        !msg.delivered_at &&
        String(senderId) !== String(currentProfileId)
      );
    })
    .map((msg) => msg.id);

  if (!incomingMessageIds.length) {
    return;
  }

  incomingMessageIds.forEach((id) => {
    pendingDeliveryIdsRef.current.add(id);
  });

  flushPendingDeliveryReceipts();
}, [currentProfileId, flushPendingDeliveryReceipts]);

const sendQueuedMessages = useCallback(async () => {
  if (!conversation?.id || !currentProfileId || !token) {
    return;
  }

  const queue = readOutgoingQueue(conversation.id, currentProfileId);

  if (!queue.length) {
    return;
  }

  const ws = socketRef.current;

  for (const queuedMessage of queue) {
    const clientTempId = queuedMessage.client_temp_id;
    const textToSend = (queuedMessage.text || "").trim();
    const replyToId =
      queuedMessage.reply_to_id || queuedMessage.reply_to?.id || null;

    if (!clientTempId || !textToSend) {
      continue;
    }

    const sendingMessage = {
      ...queuedMessage,
      local_status: "sending",
    };

    setMessages((prev) => mergeMessageIntoList(prev, sendingMessage));
    upsertOutgoingMessage(conversation.id, currentProfileId, sendingMessage);

    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(
        JSON.stringify({
          type: "message",
          text: textToSend,
          client_temp_id: clientTempId,
          reply_to_id: replyToId,
        })
      );

      continue;
    }

    try {
      const sentMessage = await sendMessage(
        conversation.id,
        textToSend,
        token,
        clientTempId,
        replyToId
      );

      setMessages((prev) => mergeMessageIntoList(prev, sentMessage));

      removeOutgoingMessage(
        conversation.id,
        currentProfileId,
        clientTempId
      );

      window.dispatchEvent(new Event("refresh-chat-list"));
    } catch (err) {
      console.error("Ошибка отправки сообщения из очереди:", err);

      const errorMessage = {
        ...queuedMessage,
        local_status: "error",
      };

      setMessages((prev) => mergeMessageIntoList(prev, errorMessage));
      upsertOutgoingMessage(conversation.id, currentProfileId, errorMessage);
    }
  }
}, [conversation?.id, currentProfileId, token]);

  // Загружаем старые сообщения.
  // GET messages больше НЕ помечает сообщения прочитанными.
  // Прочтение фиксируем отдельным markConversationRead, когда чат реально открыт.
  useEffect(() => {
    const loadMessages = async () => {
      if (!conversation?.id || !token) return;

      try {
        setMessagesLoaded(false);

        const data = await getMessages(conversation.id, token);
        const mergedMessages = mergeMessagesWithOutgoingQueue(
          conversation.id,
          currentProfileId,
          data
        );

        setMessages(mergedMessages);

        sendDeliveryReceiptsForMessages(data);

        await markConversationReadIfActive();
        setMessagesLoaded(true);
      } catch (err) {
        console.error("Ошибка загрузки сообщений:", err);
        setMessagesLoaded(true);
      }
    };

    loadMessages();
  }, [
    conversation?.id,
    token,
    currentProfileId,
    onConversationRead,
    sendDeliveryReceiptsForMessages,
    markConversationReadIfActive,
  ]);    

 // Настраиваем WebSocket с автопереподключением
  useEffect(() => {
    if (!conversation?.id || !token) return;

    shouldReconnectRef.current = true;
    reconnectAttemptRef.current = 0;

    const reconnectDelays = [1000, 2000, 5000, 10000, 15000];

    const syncMessages = async () => {
      try {
        const data = await getMessages(conversation.id, token);
        const mergedMessages = mergeMessagesWithOutgoingQueue(
          conversation.id,
          currentProfileId,
          data
        );

        setMessages(mergedMessages);

        sendDeliveryReceiptsForMessages(data);

        await markConversationReadIfActive();
      } catch (err) {
        console.error("Ошибка синхронизации сообщений:", err);
      }
    };

    const connectWebSocket = () => {
      if (!shouldReconnectRef.current) return;

      const wsUrl = `${WS_BASE_URL}/ws/chat/${conversation.id}/?token=${token}`;
      const ws = new WebSocket(wsUrl);

      socketRef.current = ws;
      setSocket(ws);

      ws.onopen = async () => {
        console.log("✅ ChatWindow WebSocket открыт:", wsUrl);
        reconnectAttemptRef.current = 0;

        flushPendingDeliveryReceipts();

        // Сначала догружаем сообщения с сервера.
        // Это может очистить очередь, если сообщение уже было сохранено.
        await syncMessages();

        // Потом отправляем только то, что реально осталось в очереди.
        await sendQueuedMessages();
      };

      ws.onmessage = async (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === "chat.message") {
            setMessages((prev) => mergeMessageIntoList(prev, data));

            if (
              data.client_temp_id &&
              String(data.sender_id) === String(currentProfileId)
            ) {
              removeOutgoingMessage(
                conversation.id,
                currentProfileId,
                data.client_temp_id
              );
            }

            window.dispatchEvent(new Event("refresh-chat-list"));

            const isIncomingMessage =
              String(data.sender_id) !== String(currentProfileId);

            if (isIncomingMessage) {
              try {
                sendDeliveryReceiptsForMessages([data]);

                await markConversationReadIfActive();
              } catch (err) {
                console.error("Ошибка mark-read через WebSocket:", err);
              }
            }
          }

          if (data.type === "chat.message_deleted") {
            if (data.message) {
              setMessages((prev) => mergeMessageIntoList(prev, data.message));
            }

            setMediaViewerIndex(null);

            window.dispatchEvent(new Event("refresh-chat-list"));
            window.dispatchEvent(new Event("refresh-unread-count"));
          }

          if (data.type === "message.status_updated") {
            setMessages((prev) =>
              prev.map((msg) => {
                const isTarget = (data.message_ids || []).some(
                  (id) => String(id) === String(msg.id)
                );

                if (!isTarget) {
                  return msg;
                }

                return {
                  ...msg,
                  delivered_at: data.delivered_at ?? msg.delivered_at,
                  is_read:
                    typeof data.is_read === "boolean"
                      ? data.is_read
                      : msg.is_read,
                  read_at: data.read_at ?? msg.read_at,
                };
              })
            );
          }
        } catch (err) {
          console.error("Ошибка обработки WebSocket сообщения:", err);
        }
      };

      ws.onerror = (event) => {
        console.error("❌ ChatWindow WebSocket ошибка:", event);
      };

      ws.onclose = (event) => {
        console.log("❌ ChatWindow WebSocket закрыт:", {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
          attempt: reconnectAttemptRef.current + 1,
        });

        if (socketRef.current === ws) {
          socketRef.current = null;
          setSocket(null);
        }

        if (!shouldReconnectRef.current) return;

        const delay =
          reconnectDelays[
            Math.min(reconnectAttemptRef.current, reconnectDelays.length - 1)
          ];

        reconnectAttemptRef.current += 1;

        reconnectTimerRef.current = setTimeout(() => {
          connectWebSocket();
        }, delay);
      };
    };

    connectWebSocket();

    return () => {
      shouldReconnectRef.current = false;

      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }

      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }

      setSocket(null);
    };
  }, [
    conversation?.id,
    token,
    currentProfileId,
    onConversationRead,
    flushPendingDeliveryReceipts,
    sendDeliveryReceiptsForMessages,
    sendQueuedMessages,
    markConversationReadIfActive,
  ]);

  useEffect(() => {
    if (!conversation?.id) return;

    const handleMessageStatusUpdated = (event) => {
      const data = event.detail;

      if (!data) return;

      if (String(data.conversation_id) !== String(conversation.id)) {
        return;
      }

      setMessages((prev) =>
        prev.map((msg) => {
          const isTarget = (data.message_ids || []).some(
            (id) => String(id) === String(msg.id)
          );

          if (!isTarget) {
            return msg;
          }

          return {
            ...msg,
            delivered_at: data.delivered_at ?? msg.delivered_at,
            is_read:
              typeof data.is_read === "boolean"
                ? data.is_read
                : msg.is_read,
            read_at: data.read_at ?? msg.read_at,
          };
        })
      );
    };

    window.addEventListener(
      "chat-message-status-updated",
      handleMessageStatusUpdated
    );

    return () => {
      window.removeEventListener(
        "chat-message-status-updated",
        handleMessageStatusUpdated
      );
    };
  }, [conversation?.id]);

  // Когда телефон/браузер возвращается из фона — догружаем пропущенные сообщения
  useEffect(() => {
    if (!conversation?.id || !token) return;

    const syncMessagesOnResume = async () => {
      if (document.visibilityState !== "visible") return;

      try {
        const data = await getMessages(conversation.id, token);
        const mergedMessages = mergeMessagesWithOutgoingQueue(
          conversation.id,
          currentProfileId,
          data
        );

        setMessages(mergedMessages);

        sendDeliveryReceiptsForMessages(data);

        await markConversationReadIfActive();
      } catch (err) {
        console.error("Ошибка синхронизации сообщений после возврата:", err);
      }
    };

    document.addEventListener("visibilitychange", syncMessagesOnResume);
    window.addEventListener("focus", syncMessagesOnResume);
    window.addEventListener("online", sendQueuedMessages);

    return () => {
      document.removeEventListener("visibilitychange", syncMessagesOnResume);
      window.removeEventListener("focus", syncMessagesOnResume);
      window.removeEventListener("online", sendQueuedMessages);
    };
  }, [
    conversation?.id,
    token,
    currentProfileId,
    onConversationRead,
    sendDeliveryReceiptsForMessages,
    sendQueuedMessages,
    markConversationReadIfActive,
  ]);

  // При смене диалога сбрасываем UI-состояние.
  useEffect(() => {
    previousMessagesLengthRef.current = 0;
    setOpenMessageMenuId(null);
    setMobileActionMessage(null);
    setReplyingToMessage(null);
    setMediaViewerIndex(null);
  }, [conversation?.id]);

  // Скроллим вниз только когда количество сообщений выросло.
  // При удалении сообщение меняется, но количество не меняется — вниз не прыгаем.
  useEffect(() => {
    if (messages.length > previousMessagesLengthRef.current) {
      scrollToBottom();
    }

    previousMessagesLengthRef.current = messages.length;
  }, [messages.length]);

  useEffect(() => {
    if (!conversation?.id) {
      return;
    }

    const timer = setTimeout(() => {
      const input = messageInputRef.current;

      if (!input) {
        return;
      }

      try {
        input.focus({ preventScroll: true });
      } catch (err) {
        input.focus();
      }
    }, 120);

    return () => clearTimeout(timer);
  }, [conversation?.id]);

  useEffect(() => {
    if (!showEmojiPicker) {
      return;
    }

    const handleClickOutsideEmojiPicker = (event) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target)
      ) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutsideEmojiPicker);
    document.addEventListener("touchstart", handleClickOutsideEmojiPicker);

    return () => {
      document.removeEventListener("mousedown", handleClickOutsideEmojiPicker);
      document.removeEventListener("touchstart", handleClickOutsideEmojiPicker);
    };
  }, [showEmojiPicker]);

  useEffect(() => {
    if (!showAttachmentMenu) {
      return;
    }

    const handleClickOutsideAttachmentMenu = (event) => {
      if (
        attachmentMenuRef.current &&
        !attachmentMenuRef.current.contains(event.target)
      ) {
        setShowAttachmentMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutsideAttachmentMenu);
    document.addEventListener("touchstart", handleClickOutsideAttachmentMenu);

    return () => {
      document.removeEventListener("mousedown", handleClickOutsideAttachmentMenu);
      document.removeEventListener("touchstart", handleClickOutsideAttachmentMenu);
    };
  }, [showAttachmentMenu]);

  const handlePickQuickReply = (quickText) => {
    setText(quickText);
    setShowEmojiPicker(false);

    setTimeout(() => {
      const input = messageInputRef.current;

      if (!input) {
        return;
      }

      input.focus();

      const cursorPosition = quickText.length;
      input.setSelectionRange(cursorPosition, cursorPosition);
    }, 0);
  };

  const createMessageClientTempId = () => {
    return `msg-${conversation.id}-${currentProfileId}-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 10)}`;
  };

  const handlePickAttachmentOption = (inputRef) => {
    setShowAttachmentMenu(false);
    setShowEmojiPicker(false);
    inputRef.current?.click();
  };

  const handleUploadFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);

    if (!files.length || !conversation?.id || !token) {
      return;
    }

    const clientTempId = createMessageClientTempId();
    const textToSend = text.trim();
    const replyToId = replyingToMessage?.id || null;

    setIsUploadingAttachment(true);
    setShowAttachmentMenu(false);
    setShowEmojiPicker(false);

    try {
      const sentMessage = await uploadChatAttachments(
        conversation.id,
        files,
        token,
        clientTempId,
        textToSend,
        replyToId
      );

      setMessages((prev) => mergeMessageIntoList(prev, sentMessage));
      setText("");
      setReplyingToMessage(null);

      window.dispatchEvent(new Event("refresh-chat-list"));

      setTimeout(() => {
        messageInputRef.current?.focus();
      }, 0);
    } catch (err) {
      console.error("Ошибка загрузки вложения:", err);
      alert("Δεν ήταν δυνατή η αποστολή του αρχείου.");
    } finally {
      setIsUploadingAttachment(false);
    }
  };

  const handleAttachmentInputChange = async (event) => {
    const selectedFiles = event.target.files;

    await handleUploadFiles(selectedFiles);

    event.target.value = "";
  };

  const handlePickEmoji = (emojiData) => {
    const pickedEmoji = emojiData?.emoji;

    if (!pickedEmoji) {
      return;
    }

    setText((prev) => `${prev}${pickedEmoji}`);

    setTimeout(() => {
      messageInputRef.current?.focus();
    }, 0);
  };

  // Отправка сообщения
  const handleSend = async (e) => {
    e.preventDefault();

    const textToSend = text.trim();
    if (!textToSend) return;

    const clientTempId = createMessageClientTempId();
    const replyToId = replyingToMessage?.id || null;
    const replySnapshot = buildReplySnapshot(replyingToMessage, currentProfileId);

    const localMessage = {
      id: null,
      client_temp_id: clientTempId,
      text: textToSend,
      sender_id: currentProfileId,
      created_at: new Date().toISOString(),
      delivered_at: null,
      is_read: false,
      read_at: null,
      local_status: "sending",
      reply_to_id: replyToId,
      reply_to: replySnapshot,
    };

    setMessages((prev) => mergeMessageIntoList(prev, localMessage));
    upsertOutgoingMessage(conversation.id, currentProfileId, localMessage);
    setText("");
    setReplyingToMessage(null);
    setShowEmojiPicker(false);

    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(
        JSON.stringify({
          type: "message",
          text: textToSend,
          client_temp_id: clientTempId,
          reply_to_id: replyToId,
        })
      );

      return;
    }

    try {
      const sentMessage = await sendMessage(
        conversation.id,
        textToSend,
        token,
        clientTempId,
        replyToId
      );

      setMessages((prev) => mergeMessageIntoList(prev, sentMessage));

      removeOutgoingMessage(
        conversation.id,
        currentProfileId,
        clientTempId
      );

      window.dispatchEvent(new Event("refresh-chat-list"));
    } catch (err) {
      console.error("Ошибка отправки сообщения через API:", err);

      const errorMessage = {
        ...localMessage,
        local_status: "error",
      };

      setMessages((prev) => mergeMessageIntoList(prev, errorMessage));

      upsertOutgoingMessage(
        conversation.id,
        currentProfileId,
        errorMessage
      );
    }
  };

  const retryMessage = async (messageToRetry) => {
    const clientTempId = messageToRetry.client_temp_id;
    const textToSend = (messageToRetry.text || "").trim();
    const replyToId =
      messageToRetry.reply_to_id || messageToRetry.reply_to?.id || null;

    if (!clientTempId || !textToSend) {
      return;
    }

    setMessages((prev) =>
      prev.map((msg) => {
        if (msg.client_temp_id !== clientTempId) {
          return msg;
        }

        return {
          ...msg,
          local_status: "sending",
        };
      })
    );

    if (socket && socket.readyState === WebSocket.OPEN) {
      try {
        socket.send(
          JSON.stringify({
            type: "message",
            text: textToSend,
            client_temp_id: clientTempId,
            reply_to_id: replyToId,
          })
        );

        return;
      } catch (err) {
        console.error("Ошибка retry через WebSocket:", err);
      }
    }

    try {
      const sentMessage = await sendMessage(
        conversation.id,
        textToSend,
        token,
        clientTempId,
        replyToId
      );

      setMessages((prev) => mergeMessageIntoList(prev, sentMessage));

      removeOutgoingMessage(
        conversation.id,
        currentProfileId,
        clientTempId
      );

      window.dispatchEvent(new Event("refresh-chat-list"));
    } catch (err) {
      console.error("Ошибка retry через API:", err);

      setMessages((prev) =>
        prev.map((msg) => {
          if (msg.client_temp_id !== clientTempId) {
            return msg;
          }

          return {
            ...msg,
            local_status: "error",
          };
        })
      );
    }
  };

  const handleDeleteMessage = async (messageToDelete) => {
    if (!messageToDelete?.id || !token) {
      return;
    }

    const confirmed = window.confirm(
      "Να διαγραφεί αυτό το μήνυμα για όλους;"
    );

    if (!confirmed) {
      return;
    }

    setOpenMessageMenuId(null);
    setMobileActionMessage(null);
    setDeletingMessageId(messageToDelete.id);

    try {
      const deletedMessage = await deleteChatMessage(messageToDelete.id, token);

      setMessages((prev) => mergeMessageIntoList(prev, deletedMessage));

      window.dispatchEvent(new Event("refresh-chat-list"));
      window.dispatchEvent(new Event("refresh-unread-count"));
    } catch (err) {
      console.error("Ошибка удаления сообщения:", err);
      alert("Δεν ήταν δυνατή η διαγραφή του μηνύματος.");
    } finally {
      setDeletingMessageId(null);
    }
  };

  const handleReplyToMessage = (messageToReply) => {
    if (!messageToReply?.id) {
      return;
    }

    setReplyingToMessage(messageToReply);
    setOpenMessageMenuId(null);
    setMobileActionMessage(null);
    setShowEmojiPicker(false);
    setShowAttachmentMenu(false);

    setTimeout(() => {
      messageInputRef.current?.focus();
    }, 0);
  };

  const clearMessageLongPressTimer = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const isTouchLikeDevice = () => {
    if (typeof window === "undefined") {
      return false;
    }

    return (
      window.matchMedia?.("(pointer: coarse)").matches ||
      "ontouchstart" in window
    );
  };

  const handleMessageLongPressStart = (messageToOpen, canShowMessageActions) => {
    if (!canShowMessageActions) {
      return;
    }

    clearMessageLongPressTimer();

    longPressTimerRef.current = setTimeout(() => {
      setMobileActionMessage(messageToOpen);
      setOpenMessageMenuId(null);
      setShowEmojiPicker(false);
      setShowAttachmentMenu(false);

      if (navigator.vibrate) {
        navigator.vibrate(8);
      }
    }, 520);
  };
    

  //  Собеседник — тот, кто не я
  const partner = String(conversation.a.id) === String(currentProfileId)
    ? conversation.b
    : conversation.a;

  const partnerStatusLabel = partner.is_online
    ? "Ενεργώς"
    : formatProfileStatus(partner.last_seen);

  const adTitle = conversation.ad_title || "Χωρίς τίτλο";
  const adPrice = conversation.ad_price;
  const adLine = adPrice ? `${adTitle} · ${adPrice} €` : adTitle;

  const hasRegularMessages = messages.some((msg) => {
    const hasText = (msg.text || "").trim();
    const hasAttachments =
      Array.isArray(msg.attachments) && msg.attachments.length > 0;

    return !msg.is_system && (hasText || hasAttachments);
  });

  const showQuickReplies =
    messagesLoaded &&
    !hasRegularMessages &&
    !text.trim();

    const mediaAttachments = getMediaAttachmentsFromMessages(messages);

    const activeMedia =
      mediaViewerIndex !== null ? mediaAttachments[mediaViewerIndex] : null;

    const openMediaViewer = (attachmentId) => {
      const nextIndex = mediaAttachments.findIndex(
        (attachment) => String(attachment.id) === String(attachmentId)
      );

      if (nextIndex === -1) {
        return;
      }

      setMediaViewerIndex(nextIndex);
    };

    const closeMediaViewer = () => {
      setMediaViewerIndex(null);
    };

    const showPreviousMedia = () => {
      setMediaViewerIndex((prev) => {
        if (prev === null || !mediaAttachments.length) {
          return prev;
        }

        return (prev - 1 + mediaAttachments.length) % mediaAttachments.length;
      });
    };

    const showNextMedia = () => {
      setMediaViewerIndex((prev) => {
        if (prev === null || !mediaAttachments.length) {
          return prev;
        }

        return (prev + 1) % mediaAttachments.length;
      });
    };

    useEffect(() => {
      if (
        mediaViewerIndex !== null &&
        mediaViewerIndex >= mediaAttachments.length
      ) {
        setMediaViewerIndex(null);
      }
    }, [mediaViewerIndex, mediaAttachments.length]);

    useEffect(() => {
      if (mediaViewerIndex === null) {
        return;
      }

      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";

      const handleKeyDown = (event) => {
        if (event.key === "Escape") {
          setMediaViewerIndex(null);
        }

        if (event.key === "ArrowLeft") {
          setMediaViewerIndex((prev) => {
            if (prev === null || !mediaAttachments.length) {
              return prev;
            }

            return (prev - 1 + mediaAttachments.length) % mediaAttachments.length;
          });
        }

        if (event.key === "ArrowRight") {
          setMediaViewerIndex((prev) => {
            if (prev === null || !mediaAttachments.length) {
              return prev;
            }

            return (prev + 1) % mediaAttachments.length;
          });
        }
      };

      window.addEventListener("keydown", handleKeyDown);

      return () => {
        document.body.style.overflow = originalOverflow;
        window.removeEventListener("keydown", handleKeyDown);
      };
    }, [mediaViewerIndex, mediaAttachments.length]);

  const mobileActionMessageIsDeleted = Boolean(
    mobileActionMessage?.is_deleted || mobileActionMessage?.deleted_at
  );

  const mobileActionMessageIsMine =
    mobileActionMessage &&
    (
      String(mobileActionMessage.sender?.id) === String(currentProfileId) ||
      String(mobileActionMessage.sender_id) === String(currentProfileId)
    );

  const canReplyToMobileActionMessage =
    mobileActionMessage &&
    mobileActionMessage.id &&
    !mobileActionMessage.is_system &&
    !mobileActionMessageIsDeleted &&
    mobileActionMessage.local_status !== "sending";

  const canDeleteMobileActionMessage =
    mobileActionMessageIsMine &&
    canReplyToMobileActionMessage;

  const isDeletingMobileActionMessage =
    mobileActionMessage?.id &&
    deletingMessageId &&
    String(deletingMessageId) === String(mobileActionMessage.id);  

  return (
    <div className="chatwindow-container">
      <div className="chatwindow-header">
        <button className="chatwindow-back" onClick={onBack}>←</button>

        <div className="chatwindow-thumb-wrapper">
          {conversation.ad_image ? (
            <img
              src={normalize(conversation.ad_image)}
              alt={adTitle}
              className="chatwindow-ad-image"
            />
          ) : (
            <div className="chatwindow-ad-placeholder">χωρίς φωτο</div>
          )}

          <img
            src={normalize(partner.avatar)}
            alt={partner.name || "profile"}
            className="chatwindow-avatar-badge"
          />
        </div>

        <div className="chatwindow-header-info">
          <div className="chatwindow-title-row">
            <span className="chatwindow-name">
              {partner.name || "Χωρίς όνομα"}
            </span>

            {partnerStatusLabel && (
              <span
                className={`chatwindow-status ${
                  partnerStatusLabel === "Ενεργώς" ? "online" : ""
                }`}
              >
                {partnerStatusLabel}
              </span>
            )}
          </div>

          <div className="chatwindow-ad-line">{adLine}</div>
        </div>
      </div>

      <div className="chatwindow-messages">
        {messages.map((msg) => {
          if (msg.is_system) {
            return (
              <div
                key={msg.id || msg.client_temp_id}
                className="chatwindow-system-message"
              >
                <div className="chatwindow-system-bubble">{msg.text}</div>
              </div>
            );
          }

          const isMine =
            (msg.sender && msg.sender.id === currentProfileId) ||
            msg.sender_id === currentProfileId;

          const statusClass = msg.local_status === "error"
            ? "error"
            : msg.read_at || msg.is_read
            ? "read"
            : msg.delivered_at
            ? "delivered"
            : msg.id
            ? "sent"
            : "sending";

          const statusLabel = msg.local_status === "error"
            ? "⚠️"
            : msg.read_at || msg.is_read
            ? "✓✓"
            : msg.delivered_at
            ? "✓✓"
            : msg.id
            ? "✓"
            : "";

          const attachments = Array.isArray(msg.attachments) ? msg.attachments : [];
          const hasAttachments = attachments.length > 0;
          const messageIsDeleted = Boolean(msg.is_deleted || msg.deleted_at);
          const messageKey = String(msg.id || msg.client_temp_id);
          const canReplyToMessage =
            msg.id &&
            !msg.is_system &&
            !messageIsDeleted &&
            msg.local_status !== "sending";

          const canDeleteMessage =
            isMine &&
            canReplyToMessage;

          const canShowMessageActions =
            canReplyToMessage || canDeleteMessage;

          const isDeletingMessage =
            deletingMessageId &&
            String(deletingMessageId) === String(msg.id);            

          return (
            <div
              key={msg.id || msg.client_temp_id}
              className={`chatwindow-message ${isMine ? "mine" : "theirs"}`}
              onTouchStart={() =>
                handleMessageLongPressStart(msg, canShowMessageActions)
              }
              onTouchMove={clearMessageLongPressTimer}
              onTouchEnd={clearMessageLongPressTimer}
              onTouchCancel={clearMessageLongPressTimer}
              onContextMenu={(event) => {
                if (!canShowMessageActions) {
                  return;
                }

                event.preventDefault();

                if (isTouchLikeDevice()) {
                  setMobileActionMessage(msg);
                  setOpenMessageMenuId(null);
                  return;
                }

                setOpenMessageMenuId(messageKey);
              }}
            >
              {canShowMessageActions && (
                <div
                  className={`message-action-wrapper ${
                    openMessageMenuId === messageKey ? "is-open" : ""
                  }`}
                  onClick={(event) => event.stopPropagation()}
                >
                  <button
                    type="button"
                    className="message-action-button"
                    onClick={(event) => {
                      event.stopPropagation();
                      setOpenMessageMenuId((prev) =>
                        prev === messageKey ? null : messageKey
                      );
                    }}
                    aria-label="Message actions"
                  >
                    ⋯
                  </button>

                  {openMessageMenuId === messageKey && (
                    <div className="message-action-menu">
                      {canReplyToMessage && (
                        <button
                          type="button"
                          onClick={() => handleReplyToMessage(msg)}
                        >
                          Απάντηση
                        </button>
                      )}

                      {canDeleteMessage && (
                        <button
                          type="button"
                          className="message-action-delete"
                          onClick={() => handleDeleteMessage(msg)}
                          disabled={isDeletingMessage}
                        >
                          {isDeletingMessage ? "Διαγραφή..." : "Διαγραφή"}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div
                className={`bubble ${isEmojiOnlyMessage(msg.text) ? "emoji-only" : ""} ${
                  hasAttachments ? "with-attachments" : ""
                } ${msg.reply_to && !messageIsDeleted ? "with-reply" : ""} ${
                  messageIsDeleted ? "is-deleted" : ""
                }`}
              >
              {msg.reply_to && !messageIsDeleted && (
                <div className="bubble-reply-preview">
                  <span className="bubble-reply-author">
                    {getReplyAuthorName(msg.reply_to, currentProfileId)}
                  </span>

                  <span className="bubble-reply-text">
                    {getReplyPreviewText(msg.reply_to)}
                  </span>
                </div>
              )}
                {hasAttachments && (
                  <div className="bubble-attachments">
                    {attachments.map((attachment) =>
                      renderChatAttachment(attachment, openMediaViewer)
                    )}
                  </div>
                )}

                {(msg.text || "").trim() && (
                  <span className="bubble-text">{renderMessageText(msg.text)}</span>
                )}

                {isMine && statusLabel && (
                  <span className={`message-status ${statusClass}`}>
                    {statusLabel}
                  </span>
                )}

                {isMine && msg.local_status === "error" && (
                  <button
                    type="button"
                    className="message-retry-button"
                    onClick={() => retryMessage(msg)}
                  >
                    Επανάληψη
                  </button>
                )}
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {showQuickReplies && (
        <div className="chatwindow-quick-replies">
          {QUICK_REPLY_TEXTS.map((quickText) => (
            <button
              key={quickText}
              type="button"
              className="chatwindow-quick-reply"
              onClick={() => handlePickQuickReply(quickText)}
            >
              {quickText}
            </button>
          ))}
        </div>
      )}

      {replyingToMessage && (
        <div className="chatwindow-replying-bar">
          <div className="chatwindow-replying-content">
            <span className="chatwindow-replying-label">
              Απάντηση σε {getReplyAuthorName(replyingToMessage, currentProfileId)}
            </span>

            <span className="chatwindow-replying-text">
              {getReplyPreviewText(replyingToMessage)}
            </span>
          </div>

          <button
            type="button"
            className="chatwindow-replying-close"
            onClick={() => setReplyingToMessage(null)}
            aria-label="Cancel reply"
          >
            ×
          </button>
        </div>
      )}

      <form className="chatwindow-input" onSubmit={handleSend}>
        <div className="chatwindow-emoji-wrapper" ref={emojiPickerRef}>
          <button
            type="button"
            className="chatwindow-emoji-button"
            onClick={() => setShowEmojiPicker((prev) => !prev)}
            aria-label="Emoji"
          >
            😊
          </button>

          {showEmojiPicker && (
            <div className="chatwindow-emoji-picker">
              <EmojiPicker
                onEmojiClick={handlePickEmoji}
                width="100%"
                height={360}
                searchPlaceholder="Αναζήτηση emoji..."
                previewConfig={{ showPreview: false }}
              />
            </div>
          )}
        </div>

         <div className="chatwindow-attachment-wrapper" ref={attachmentMenuRef}>
          <button
            type="button"
            className="chatwindow-plus-button"
            onClick={() => {
              setShowAttachmentMenu((prev) => !prev);
              setShowEmojiPicker(false);
            }}
            disabled={isUploadingAttachment}
            aria-label="Add attachment"
          >
            {isUploadingAttachment ? "…" : "+"}
          </button>

          {showAttachmentMenu && (
            <div className="chatwindow-attachment-menu">
              <button
                type="button"
                onClick={() => handlePickAttachmentOption(mediaInputRef)}
              >
                Φωτογραφία / βίντεο
              </button>

              <button
                type="button"
                onClick={() => handlePickAttachmentOption(fileInputRef)}
              >
                Αρχείο
              </button>

              <button
                type="button"
                onClick={() => handlePickAttachmentOption(cameraImageInputRef)}
              >
                Λήψη φωτογραφίας
              </button>

              <button
                type="button"
                onClick={() => handlePickAttachmentOption(cameraVideoInputRef)}
              >
                Λήψη βίντεο
              </button>
            </div>
          )}

          <input
            ref={mediaInputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            className="chatwindow-hidden-file-input"
            onChange={handleAttachmentInputChange}
          />

          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.txt,.doc,.docx,.xls,.xlsx,application/pdf,text/plain,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            multiple
            className="chatwindow-hidden-file-input"
            onChange={handleAttachmentInputChange}
          />

          <input
            ref={cameraImageInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="chatwindow-hidden-file-input"
            onChange={handleAttachmentInputChange}
          />

          <input
            ref={cameraVideoInputRef}
            type="file"
            accept="video/*"
            capture="environment"
            className="chatwindow-hidden-file-input"
            onChange={handleAttachmentInputChange}
          />
        </div>

        <input
          ref={messageInputRef}
          type="text"
          placeholder="Γράψε ένα μήνυμα..."
          value={text}
          onChange={(e) => setText(e.target.value)}
        />

        <button type="submit" className="chatwindow-send-button">
          Αποστολή
        </button>
      </form>

      {mobileActionMessage && (
        <div
          className="mobile-message-action-backdrop"
          onClick={() => setMobileActionMessage(null)}
        >
          <div
            className="mobile-message-action-sheet"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mobile-message-action-title">
              {getReplyPreviewText(mobileActionMessage)}
            </div>

            {canReplyToMobileActionMessage && (
              <button
                type="button"
                onClick={() => handleReplyToMessage(mobileActionMessage)}
              >
                Απάντηση
              </button>
            )}

            {canDeleteMobileActionMessage && (
              <button
                type="button"
                className="mobile-message-action-delete"
                onClick={() => handleDeleteMessage(mobileActionMessage)}
                disabled={isDeletingMobileActionMessage}
              >
                {isDeletingMobileActionMessage ? "Διαγραφή..." : "Διαγραφή"}
              </button>
            )}

            <button
              type="button"
              className="mobile-message-action-cancel"
              onClick={() => setMobileActionMessage(null)}
            >
              Άκυρο
            </button>
          </div>
        </div>
      )}

      {activeMedia && (
        <div
          className="chat-media-viewer"
          role="dialog"
          aria-modal="true"
          onClick={closeMediaViewer}
        >
          <button
            type="button"
            className="chat-media-viewer-close"
            onClick={closeMediaViewer}
            aria-label="Close"
          >
            ×
          </button>

          {mediaAttachments.length > 1 && (
            <>
              <button
                type="button"
                className="chat-media-viewer-nav previous"
                onClick={(event) => {
                  event.stopPropagation();
                  showPreviousMedia();
                }}
                aria-label="Previous media"
              >
                ‹
              </button>

              <button
                type="button"
                className="chat-media-viewer-nav next"
                onClick={(event) => {
                  event.stopPropagation();
                  showNextMedia();
                }}
                aria-label="Next media"
              >
                ›
              </button>
            </>
          )}

          <div
            className="chat-media-viewer-content"
            onClick={(event) => event.stopPropagation()}
          >
            {activeMedia.attachment_type === "image" ? (
              <img
                src={activeMedia.url}
                alt={activeMedia.original_name || "image"}
                className="chat-media-viewer-image"
              />
            ) : (
              <video
                src={activeMedia.url}
                className="chat-media-viewer-video"
                controls
                playsInline
                autoPlay
              />
            )}

            <div className="chat-media-viewer-footer">
              <span className="chat-media-viewer-name">
                {activeMedia.original_name || ""}
              </span>

              <span className="chat-media-viewer-count">
                {mediaViewerIndex + 1} / {mediaAttachments.length}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatWindow;